// Feb 18, 2026 — Affiliate's "What you earned this month" card.
//
// Replaces the dead "Starter/Growing" bracket banner. Surfaces the REAL
// engine instead of a fantasy tier ladder:
//   - Recurring rev-share (25% / 30% of paying referees' platform spend)
//   - DFY operator share (if they've taken any DFY assignments)
//
// Affiliate-only — non-affiliates don't see this (handled by parent
// <RoleGate role="affiliate">).
import { useEffect, useState } from "react";
import { Sparkle, TrendUp, CurrencyDollar, Briefcase } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmt$ } from "@/lib/usePublicPricing";

const KIND_META = {
  // Feb 21, 2026 — all live source_kind values from affiliate_payouts.
  // The card uses these to render a friendly label + icon for each
  // breakdown box. Anything not here falls back to "rev-share".
  // Oct 4, 2026 (Randy) — "affiliate" is retired from user-facing copy;
  // these read "Creator Pro" now. The DB source_kind values are unchanged.
  // `affiliate_gate_addon` was missing entirely and fell through to the
  // generic fallback, so add-on earnings showed up unlabelled.
  affiliate_gate:              { label: "Creator Pro purchase",  icon: CurrencyDollar, color: "#C16E5A" },
  affiliate_gate_retro:        { label: "Creator Pro · retro",   icon: CurrencyDollar, color: "#C16E5A" },
  affiliate_gate_addon:        { label: "Creator Pro add-on",    icon: CurrencyDollar, color: "#C16E5A" },
  affiliate_gate_forfeit:      { label: "Forfeited (fallback)",  icon: CurrencyDollar, color: "#6B6A68" },
  platform_subscription:       { label: "Recurring rev-share",  icon: TrendUp,        color: "#4A5D4E" },
  platform_subscription_retro: { label: "First-month · retro",  icon: TrendUp,        color: "#A8662F" },
  rev_share:                   { label: "Recurring rev-share",  icon: TrendUp,        color: "#4A5D4E" },
  dfy_operator:                { label: "DFY delivery",         icon: Briefcase,      color: "#1C1B1A" },
};

export default function EarnedThisMonthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/me/earnings-this-month")
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { /* render-empty */ })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading) return null;
  if (!data?.is_affiliate) return null;

  const monthName = new Date(data.month_start).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const total = data.total_cents || 0;
  const breakdownEntries = Object.entries(data.by_kind || {});

  return (
    <div
      className="bg-gradient-to-br from-[#1C1B1A] to-[#3A4A3E] text-[#FDFCF8] rounded-xl p-6 mb-6 relative overflow-hidden"
      data-testid="earned-this-month-card"
    >
      <div className="absolute top-3 right-3 opacity-15"><CurrencyDollar size={72} weight="duotone" /></div>
      <div className="relative z-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#F5E9D0] mb-2 flex items-center gap-1.5">
          <Sparkle size={11} weight="duotone" className="text-[#E8C170]" /> {monthName} · so far
        </div>
        <h3 className="font-serif-display text-3xl text-white" data-testid="earned-month-total">
          {fmt$(total)}
        </h3>
        <p className="text-xs text-[#F5E9D0] mt-1 max-w-xl">
          {total > 0
            ? "Live tally of your Partner earnings this month. Settles into your withdrawable balance as referees pay their bills."
            : "Nothing earned yet this month. Once a referee pays a platform bill, your share lands here automatically."}
        </p>

        {breakdownEntries.length > 0 && (
          <div className="mt-5" data-testid="earned-breakdown-wrap">
            {/* Feb 21, 2026 — vertical stack with max-height scroll. With
                potentially dozens of source_kind categories as a user's
                affiliate base scales, the original 3-col grid would blow
                up the card. Now: show as a scrollable list capped to ~3
                rows visible (~50px each) then scroll. */}
            <div className={`space-y-2 ${breakdownEntries.length > 3 ? "max-h-[180px] overflow-y-auto pr-1" : ""}`}
              data-testid="earned-breakdown">
              {breakdownEntries.map(([kind, val]) => {
                const meta = KIND_META[kind] || { label: kind, icon: CurrencyDollar, color: "#6B6A68" };
                const Icon = meta.icon;
                return (
                  <div key={kind} className="bg-white/5 backdrop-blur-sm rounded-lg px-3 py-2.5 border border-white/10 flex items-center gap-3">
                    <Icon size={14} weight="duotone" style={{ color: meta.color === "#1C1B1A" ? "#FDFCF8" : meta.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-white/85">{meta.label}</div>
                      <div className="text-[10px] text-white/70">{val.count} event{val.count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-base font-medium text-white tabular-nums shrink-0">{fmt$(val.total_cents)}</div>
                  </div>
                );
              })}
            </div>
            {breakdownEntries.length > 3 && (
              <div className="mt-1 text-[10px] text-white/70 text-center">
                Showing {breakdownEntries.length} categories — scroll for more
              </div>
            )}
          </div>
        )}

        {/* Feb 21, 2026 — recurring forecast strip. Tells the affiliate
            what's coming on autopilot every month from their already-
            activated referee base. This is the "$2.50/mo from Levi"
            visibility that was missing. */}
        {(data.recurring_forecast_cents || 0) > 0 && (
          <div className="mt-5 pt-4 border-t border-white/15 flex items-center gap-3 flex-wrap" data-testid="earned-recurring-forecast">
            <TrendUp size={14} weight="duotone" className="text-[#E8C170]" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/90">Recurring incoming</span>
            <span className="font-serif-display text-xl text-white tabular-nums">
              {fmt$(data.recurring_forecast_cents)}<span className="text-xs text-white/80">/mo</span>
            </span>
            <span className="text-[10px] text-white/75">
              based on your current active referees — auto-credited each time they're billed
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
